import React from 'react';

import { gql } from '@apollo/client';
import { FunctionWithFragments } from '@modernloop/shared/components';

import {
  DynamicAudience,
  UseCandidateSmartOptions_CandidateCoordinatorDataFragment,
  UseCandidateSmartOptions_CandidateRecruiterDataFragment,
  UseCandidateSmartOptions_CandidateSourcerDataFragment,
  UseCandidateSmartOptions_PreviousCandidateInterviewersDataFragment,
} from 'src/generated/mloop-graphql';

import { OptionRenderer } from './OptionRenderer';

type Fragments = {
  candidateCoordinatorData: UseCandidateSmartOptions_CandidateCoordinatorDataFragment[];
  candidateRecruiterData: UseCandidateSmartOptions_CandidateRecruiterDataFragment[];
  candidateSourcerData: UseCandidateSmartOptions_CandidateSourcerDataFragment[];
  previousCandidateInterviewersData: UseCandidateSmartOptions_PreviousCandidateInterviewersDataFragment[];
};

type HookReturn = {
  options: DynamicAudience[];
  renderer: (props: {
    optionType: DynamicAudience;
    isDisabled: boolean;
    isChecked: boolean;
    disableMultiSelect: boolean;
  }) => JSX.Element | null;
  getData: (
    optionType: DynamicAudience
  ) =>
    | UseCandidateSmartOptions_CandidateCoordinatorDataFragment[]
    | UseCandidateSmartOptions_CandidateRecruiterDataFragment[]
    | UseCandidateSmartOptions_CandidateSourcerDataFragment[]
    | UseCandidateSmartOptions_PreviousCandidateInterviewersDataFragment[]
    | undefined
    | null;
};

export const useCandidateSmartOptions: FunctionWithFragments<Fragments, null, HookReturn> = ({
  candidateCoordinatorData,
  candidateRecruiterData,
  candidateSourcerData,
  previousCandidateInterviewersData,
}) => {
  const returnOptionData = (optionType: DynamicAudience) => {
    switch (optionType) {
      case DynamicAudience.CandidateCoordinator:
        return candidateCoordinatorData;
      case DynamicAudience.CandidateRecruiter:
        return candidateRecruiterData;
      case DynamicAudience.CandidateSourcer:
        return candidateSourcerData;
      case DynamicAudience.CandidatesPreviousInterviewers:
        return previousCandidateInterviewersData;
      default:
        return null;
    }
  };

  return {
    options: [
      DynamicAudience.CandidateRecruiter,
      DynamicAudience.CandidateCoordinator,
      DynamicAudience.CandidateSourcer,
      //   DynamicAudience.PreviousCandidateInterviewers,
      DynamicAudience.CandidatesPreviousInterviewers,
    ],
    renderer: ({
      optionType,
      isDisabled,
      isChecked,
      disableMultiSelect,
    }: {
      optionType: DynamicAudience;
      isDisabled: boolean;
      isChecked: boolean;
      disableMultiSelect: boolean;
    }) => {
      const disabledForContext = isDisabled;

      switch (optionType) {
        case DynamicAudience.CandidateCoordinator:
          return (
            <OptionRenderer
              type={DynamicAudience.CandidateCoordinator}
              employees={returnOptionData(DynamicAudience.CandidateCoordinator)}
              isDisabled={disabledForContext}
              isChecked={isChecked}
              disableMultiSelect={disableMultiSelect}
            />
          );
        case DynamicAudience.CandidateRecruiter:
          return (
            <OptionRenderer
              type={DynamicAudience.CandidateRecruiter}
              employees={returnOptionData(DynamicAudience.CandidateRecruiter)}
              isDisabled={disabledForContext}
              isChecked={isChecked}
              disableMultiSelect={disableMultiSelect}
            />
          );
        case DynamicAudience.CandidateSourcer:
          return (
            <OptionRenderer
              type={DynamicAudience.CandidateSourcer}
              employees={returnOptionData(DynamicAudience.CandidateSourcer)}
              isDisabled={disabledForContext}
              isChecked={isChecked}
              disableMultiSelect={disableMultiSelect}
            />
          );
        case DynamicAudience.CandidatesPreviousInterviewers:
          return (
            <OptionRenderer
              type={DynamicAudience.CandidatesPreviousInterviewers}
              employees={returnOptionData(DynamicAudience.CandidatesPreviousInterviewers)}
              isDisabled={disabledForContext}
              isChecked={isChecked}
              disableMultiSelect={disableMultiSelect}
            />
          );
        default:
          return null;
      }
    },
    getData: (optionType: DynamicAudience) => {
      switch (optionType) {
        case DynamicAudience.CandidateCoordinator:
          return returnOptionData(DynamicAudience.CandidateCoordinator);
        case DynamicAudience.CandidateRecruiter:
          return returnOptionData(DynamicAudience.CandidateRecruiter);
        case DynamicAudience.CandidateSourcer:
          return returnOptionData(DynamicAudience.CandidateSourcer);
        case DynamicAudience.CandidatesPreviousInterviewers:
          return returnOptionData(DynamicAudience.CandidatesPreviousInterviewers);
        default:
          return [];
      }
    },
  };
};

useCandidateSmartOptions.fragments = {
  candidateCoordinatorData: gql`
    ${OptionRenderer.fragments.employees}
    fragment useCandidateSmartOptions_candidateCoordinatorData on Employee {
      ...OptionRenderer_employees
    }
  `,
  candidateRecruiterData: gql`
    ${OptionRenderer.fragments.employees}
    fragment useCandidateSmartOptions_candidateRecruiterData on Employee {
      ...OptionRenderer_employees
    }
  `,
  candidateSourcerData: gql`
    ${OptionRenderer.fragments.employees}
    fragment useCandidateSmartOptions_candidateSourcerData on Employee {
      ...OptionRenderer_employees
    }
  `,
  previousCandidateInterviewersData: gql`
    ${OptionRenderer.fragments.employees}
    fragment useCandidateSmartOptions_previousCandidateInterviewersData on Employee {
      ...OptionRenderer_employees
    }
  `,
};
